import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Activity, Tv, Bike, BookOpen, TrendingUp, Eye, Shield, Zap, Target, Lightbulb, Trophy, Users } from "lucide-react";
import { InsightCardExpanded } from "@/components/InsightCard";

// =============================================================================
// GUIDED TOUR — zero-interaction narrated walkthrough for demo link viewers
// =============================================================================

const STEPS = ["welcome", "racing", "broadcast", "cycling", "library", "assist", "closing"];

const GuidedTourPage = ({ token }) => {
  const [step, setStep] = useState("welcome");
  const [fadeIn, setFadeIn] = useState(true);
  const [raceId, setRaceId] = useState(null);

  // Racing state
  const [raceData, setRaceData] = useState(null);
  const [raceCallout, setRaceCallout] = useState(0);
  const raceStreamRef = useRef(null);

  // Broadcast state
  const [broadcastData, setBroadcastData] = useState(null);
  const broadcastStreamRef = useRef(null);

  // Cycling state
  const [cyclingData, setCyclingData] = useState(null);
  const cyclingStreamRef = useRef(null);
  const cyclingElapsedRef = useRef(0);

  // Library state (loaded from API)
  const [libraryData, setLibraryData] = useState(null);

  // Assist step data (saved from racing step)
  const [assistData, setAssistData] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}` };

  // Transition helper — fade out, change step, fade in
  const transitionTo = useCallback((nextStep) => {
    setFadeIn(false);
    setTimeout(() => {
      setStep(nextStep);
      setFadeIn(true);
    }, 600);
  }, []);

  // ---- STEP 1: Welcome — auto-advance after 5s ----
  useEffect(() => {
    if (step !== "welcome") return;
    // Pre-fetch a race ID for later steps
    const init = async () => {
      try {
        const res = await axios.get(`${API}/races/current`, { headers: authHeaders });
        if (res.data.length > 0) {
          setRaceId(res.data[0].race_id);
          // Reset the race so it starts fresh
          await axios.post(`${API}/admin/reset?race_id=${res.data[0].race_id}`, {}, { headers: authHeaders });
        }
      } catch (e) { /* silent */ }
    };
    init();
    const timer = setTimeout(() => transitionTo("racing"), 5000);
    return () => clearTimeout(timer);
  }, [step]);

  // ---- STEP 2: Racing — stream until finish, then advance ----
  const fetchRace = useCallback(async () => {
    if (!raceId) return;
    try {
      const res = await axios.get(`${API}/live/race/${raceId}`, { headers: authHeaders });
      setRaceData(res.data);
      // Save assist data from the best runner for the IAL showcase step
      if (res.data.field?.length > 0) {
        const bestRunner = res.data.field.find(h => h.assist?.length > 1) || res.data.field[0];
        setAssistData({ runner: bestRunner, cards: res.data.cards });
      }
      // Auto-advance when race finishes
      if (res.data.phase === "finish") {
        if (raceStreamRef.current) clearInterval(raceStreamRef.current);
        raceStreamRef.current = null;
        // Hold finish state for 6s so viewer can read, then advance
        setTimeout(() => transitionTo("broadcast"), 6000);
      }
    } catch (e) { /* silent */ }
  }, [raceId, transitionTo]);

  useEffect(() => {
    if (step !== "racing" || !raceId) return;
    // Start streaming after a 2s pause for the step intro to breathe
    const startTimer = setTimeout(() => {
      fetchRace();
      raceStreamRef.current = setInterval(fetchRace, 900);
    }, 2000);
    // Callout progression
    const c1 = setTimeout(() => setRaceCallout(1), 3000);
    const c2 = setTimeout(() => setRaceCallout(2), 10000);
    const c3 = setTimeout(() => setRaceCallout(3), 20000);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(c1); clearTimeout(c2); clearTimeout(c3);
      if (raceStreamRef.current) clearInterval(raceStreamRef.current);
    };
  }, [step, raceId, fetchRace]);

  // ---- STEP 3: Broadcast — stream for 18s then advance ----
  const fetchBroadcast = useCallback(async () => {
    if (!raceId) return;
    try {
      // Reset race for broadcast demo
      const res = await axios.get(`${API}/broadcast/state/${raceId}`, { headers: authHeaders });
      setBroadcastData(res.data);
    } catch (e) { /* silent */ }
  }, [raceId]);

  useEffect(() => {
    if (step !== "broadcast" || !raceId) return;
    // Reset race so broadcast has fresh data
    const init = async () => {
      try { await axios.post(`${API}/admin/reset?race_id=${raceId}`, {}, { headers: authHeaders }); } catch (e) {}
      fetchBroadcast();
      broadcastStreamRef.current = setInterval(fetchBroadcast, 900);
    };
    const startTimer = setTimeout(init, 1500);
    const advanceTimer = setTimeout(() => {
      if (broadcastStreamRef.current) clearInterval(broadcastStreamRef.current);
      transitionTo("cycling");
    }, 18000);
    return () => {
      clearTimeout(startTimer); clearTimeout(advanceTimer);
      if (broadcastStreamRef.current) clearInterval(broadcastStreamRef.current);
    };
  }, [step, raceId, fetchBroadcast, transitionTo]);

  // ---- STEP 4: Cycling — stream for 18s then advance ----
  const fetchCycling = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sports/cycling/demo/stage_1?elapsed=${cyclingElapsedRef.current}`, { headers: authHeaders });
      setCyclingData(res.data);
      cyclingElapsedRef.current += 1.2;
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (step !== "cycling") return;
    const startTimer = setTimeout(() => {
      fetchCycling();
      cyclingStreamRef.current = setInterval(fetchCycling, 1200);
    }, 1500);
    const advanceTimer = setTimeout(() => {
      if (cyclingStreamRef.current) clearInterval(cyclingStreamRef.current);
      transitionTo("library");
    }, 18000);
    return () => {
      clearTimeout(startTimer); clearTimeout(advanceTimer);
      if (cyclingStreamRef.current) clearInterval(cyclingStreamRef.current);
    };
  }, [step, fetchCycling, transitionTo]);

  // ---- STEP 5: Race Library — load data, show for 18s, then IAL ----
  useEffect(() => {
    if (step !== "library") return;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/library/races`, { headers: authHeaders });
        const races = res.data.races || [];
        if (races.length > 0) {
          const detailRes = await axios.get(`${API}/library/races/${races[0].race_id}`, { headers: authHeaders });
          setLibraryData({ races, detail: detailRes.data });
        }
      } catch (e) { /* silent */ }
    };
    load();
    const timer = setTimeout(() => transitionTo("assist"), 18000);
    return () => clearTimeout(timer);
  }, [step, transitionTo]);

  // ---- STEP 6: Intelligence Assist — showcase for 15s, then closing ----
  useEffect(() => {
    if (step !== "assist") return;
    const timer = setTimeout(() => transitionTo("closing"), 15000);
    return () => clearTimeout(timer);
  }, [step, transitionTo]);

  // Cleanup all intervals on unmount
  useEffect(() => () => {
    if (raceStreamRef.current) clearInterval(raceStreamRef.current);
    if (broadcastStreamRef.current) clearInterval(broadcastStreamRef.current);
    if (cyclingStreamRef.current) clearInterval(cyclingStreamRef.current);
  }, []);

  // ---- Progress indicator ----
  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="min-h-screen bg-[#090a0d] text-white relative overflow-hidden" data-testid="guided-tour-page">
      {/* Subtle progress bar */}
      <div className="fixed top-0 left-0 right-0 h-0.5 bg-white/5 z-50">
        <div
          className="h-full bg-[#F97316] transition-all duration-1000 ease-out"
          style={{ width: `${((stepIndex + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      {/* Step indicator dots */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div
            key={s}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
              i === stepIndex ? "bg-[#F97316] w-6" : i < stepIndex ? "bg-[#F97316]/40" : "bg-white/10"
            }`}
          />
        ))}
      </div>

      {/* Content — with fade transition */}
      <div className={`transition-opacity duration-500 ${fadeIn ? "opacity-100" : "opacity-0"}`}>
        {step === "welcome" && <WelcomeStep />}
        {step === "racing" && <RacingStep data={raceData} calloutLevel={raceCallout} />}
        {step === "broadcast" && <BroadcastStep data={broadcastData} />}
        {step === "cycling" && <CyclingStep data={cyclingData} />}
        {step === "library" && <LibraryStep data={libraryData} />}
        {step === "assist" && <AssistStep data={assistData} />}
        {step === "closing" && <ClosingStep />}
      </div>
    </div>
  );
};

// =============================================================================
// STEP 1: WELCOME
// =============================================================================
const WelcomeStep = () => (
  <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="tour-welcome">
    <div className="text-center max-w-xl animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="w-16 h-16 rounded-2xl bg-[#F97316]/10 flex items-center justify-center mx-auto mb-8">
        <Activity className="w-8 h-8 text-[#F97316]" />
      </div>
      <div className="text-[#F97316] text-xs font-bold uppercase tracking-[0.3em] mb-3">Race Intelligence Live</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
        One engine, any sport.
      </h1>
      <p className="text-white/40 text-base leading-relaxed mb-8">
        You've been invited to preview our broadcast-grade intelligence platform.
        Sit back — this guided walkthrough takes about 90 seconds.
      </p>
      <div className="flex items-center justify-center gap-8 text-white/20 text-xs uppercase tracking-wider">
        <span className="flex items-center gap-2"><Activity className="w-3 h-3" /> Racing</span>
        <span className="flex items-center gap-2"><Tv className="w-3 h-3" /> Broadcast</span>
        <span className="flex items-center gap-2"><Bike className="w-3 h-3" /> Cycling</span>
        <span className="flex items-center gap-2"><BookOpen className="w-3 h-3" /> Library</span>
      </div>
    </div>
  </div>
);

// =============================================================================
// STEP 2: RACING INTELLIGENCE
// =============================================================================
const RacingStep = ({ data, calloutLevel }) => {
  const d = data;
  const isFinished = d?.phase === "finish";

  return (
    <div className="min-h-screen flex flex-col" data-testid="tour-racing">
      {/* Step header */}
      <div className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-6 h-12 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#F97316]" />
          <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Racing Intelligence</span>
          {d && (
            <span className="text-xs font-mono ml-3 px-2 py-0.5 rounded" style={{
              backgroundColor: isFinished ? "#10B98120" : "#F9731620",
              color: isFinished ? "#10B981" : "#F97316",
            }}>
              {isFinished ? "RACE COMPLETE" : d.phase?.toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {d && !isFinished && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-red-400 text-[10px] font-bold uppercase tracking-wider">Live</span>
            </div>
          )}
        </div>
      </div>

      {d ? (
        <div className="flex-1 p-6 max-w-[1400px] mx-auto w-full space-y-4">
          {/* Narrator callouts — appear progressively */}
          <NarratorCallout
            visible={calloutLevel >= 1 && !isFinished}
            text="Watch the live intelligence below. Every runner is being scored in real-time across efficiency, pressure, and momentum."
          />
          <NarratorCallout
            visible={calloutLevel >= 2 && !isFinished}
            text="Notice the True Rank column — it often diverges from the visible Finish position. That's where the value lies."
          />
          <NarratorCallout
            visible={calloutLevel >= 3 && !isFinished}
            text="The intelligence cards above identify the Best Run, Hidden Run, and runners to Forgive or Upgrade next start."
          />
          <NarratorCallout
            visible={isFinished}
            text="Race complete. Below is the post-race intelligence summary — the product our clients receive."
            colour="#10B981"
          />

          {/* Signal bar */}
          <div className="flex items-center gap-4 text-xs font-mono">
            {d.most_efficient_runner && (
              <span className="text-[#10B981] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                Efficiency Leader: {d.most_efficient_runner}
              </span>
            )}
            {d.under_pressure_runner && (
              <span className="text-[#EF4444] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                Pressure: {d.under_pressure_runner}
              </span>
            )}
            {d.emerging_threat && (
              <span className="text-[#F59E0B] flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] animate-pulse" />
                Emerging: {d.emerging_threat}
              </span>
            )}
          </div>

          {/* Progress bar */}
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded" style={{
              backgroundColor: isFinished ? "#10B98120" : "#F9731620",
              color: isFinished ? "#10B981" : "#F97316",
            }}>
              {d.phase || "—"}
            </span>
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#F97316] to-[#EF4444] transition-all duration-700"
                style={{ width: `${d.distance_m > 0 ? Math.max(2, ((d.distance_m - d.distance_remaining_m) / d.distance_m) * 100) : 0}%` }}
              />
            </div>
            <span className="text-white/40 text-xs font-mono w-24 text-right">
              {d.distance_remaining_m > 0 ? `${Math.round(d.distance_remaining_m)}m to go` : "FINISHED"}
            </span>
          </div>

          {/* What Mattered */}
          {d.what_mattered && (
            <div className="p-4 rounded-xl border border-[#F97316]/30 bg-[#F97316]/5">
              <div className="text-[#F97316] text-[10px] uppercase tracking-widest font-bold mb-1">What Mattered</div>
              <p className="text-white text-sm leading-relaxed">{d.what_mattered}</p>
            </div>
          )}

          {/* Intelligence Cards */}
          <div className="grid grid-cols-5 gap-3">
            <TourIntelCard title="Best Run" data={d.cards?.best_run} colour="#10B981" icon={TrendingUp} />
            <TourIntelCard title="Hidden Run" data={d.cards?.hidden_run} colour="#3B82F6" icon={Eye} />
            <TourIntelCard title="Forgive Run" data={d.cards?.forgive_run} colour="#A855F7" icon={Shield} />
            <TourIntelCard title="Best Closer" data={d.cards?.best_closer} colour="#F59E0B" icon={TrendingUp} />
            <TourIntelCard title="Best On Pace" data={d.cards?.best_on_pace} colour="#EF4444" icon={Zap} />
          </div>

          {/* Simplified field table — top 6 only for readability */}
          <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
              <span className="text-white/60 text-xs uppercase tracking-wider font-bold">Field Intelligence</span>
              <span className="text-white/30 text-xs">{d.field?.length} runners</span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase tracking-wider">
                  <th className="px-3 py-2 text-left w-10">No</th>
                  <th className="px-3 py-2 text-left">Horse</th>
                  <th className="px-3 py-2 text-center w-12">Fin</th>
                  <th className="px-3 py-2 text-center w-12">True</th>
                  <th className="px-3 py-2 text-center w-16">Eff</th>
                  <th className="px-3 py-2 text-center w-20">Mom</th>
                  <th className="px-3 py-2 text-left min-w-[200px]">Verdict</th>
                  <th className="px-3 py-2 text-center w-20">Next</th>
                </tr>
              </thead>
              <tbody>
                {d.field?.slice(0, 8).map((h, i) => (
                  <tr key={h.no} className={`border-b border-white/5 ${i % 2 === 0 ? "bg-white/[0.01]" : ""}`}>
                    <td className="px-3 py-2 text-white/60 font-mono text-xs">{h.no}</td>
                    <td className="px-3 py-2 text-white font-semibold text-xs">{h.horse}</td>
                    <td className="px-3 py-2 text-center text-white font-mono text-xs">{h.fin}</td>
                    <td className="px-3 py-2 text-center font-mono text-xs">
                      <span className={h.true < h.fin ? "text-[#10B981] font-bold" : h.true > h.fin ? "text-[#EF4444]" : "text-white/60"}>{h.true}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                        backgroundColor: `${h.eff === "Strong" ? "#10B981" : h.eff === "Weak" ? "#EF4444" : "#F59E0B"}15`,
                        color: h.eff === "Strong" ? "#10B981" : h.eff === "Weak" ? "#EF4444" : "#F59E0B",
                      }}>{h.eff}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                        backgroundColor: `${h.mom === "Building" ? "#10B981" : h.mom === "Faded" || h.mom === "Peaked Early" ? "#EF4444" : "#F59E0B"}15`,
                        color: h.mom === "Building" ? "#10B981" : h.mom === "Faded" || h.mom === "Peaked Early" ? "#EF4444" : "#F59E0B",
                      }}>{h.mom}</span>
                    </td>
                    <td className="px-3 py-2 text-white/60 text-xs max-w-[300px]">{h.verdict}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{
                        backgroundColor: `${h.next === "Upgrade" ? "#10B981" : h.next === "Downgrade" ? "#EF4444" : "#666"}15`,
                        color: h.next === "Upgrade" ? "#10B981" : h.next === "Downgrade" ? "#EF4444" : "#666",
                      }}>{h.next}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <span className="text-white/40 text-xs font-mono">Initialising race intelligence...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// STEP 3: BROADCAST OVERLAY
// =============================================================================
const BroadcastStep = ({ data }) => {
  const d = data;
  return (
    <div className="min-h-screen relative" style={{ backgroundColor: "#00FF00" }} data-testid="tour-broadcast">
      {/* Narrator callout — positioned above the broadcast graphics */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 max-w-lg">
        <NarratorCallout
          visible
          text="This is our broadcast overlay — compositable directly into live television feeds via chroma-key. All graphics are driven by the same intelligence engine."
          colour="#EF4444"
          inline
        />
      </div>

      {/* Step label */}
      <div className="absolute top-4 right-8 z-40 flex items-center gap-2">
        <Tv className="w-3.5 h-3.5 text-white" />
        <span className="text-white text-xs font-bold uppercase tracking-wider bg-black/80 px-2 py-1 rounded">Broadcast Overlay</span>
      </div>

      {d && (
        <>
          {/* TOP BANNER */}
          <div className="absolute top-16 left-0 right-0 z-30">
            <div className="mx-8 flex items-stretch h-10 rounded-md overflow-hidden shadow-lg">
              <div className="bg-[#F97316] px-4 flex items-center">
                <span className="text-white text-xs font-black tracking-wider uppercase">{d.track_name}</span>
              </div>
              <div className="bg-black/90 flex items-center gap-4 px-4 flex-1">
                <BroadcastChip label="PACE" value={d.race_state || "EVEN"} />
                <BroadcastChip label="PRESSURE" value={d.pressure_state || "LOW"} />
                <BroadcastChip label="BIAS" value={d.bias_state || "NEUTRAL"} />
              </div>
              <div className="bg-red-600 px-3 flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                <span className="text-white text-[10px] font-black tracking-wider">LIVE</span>
              </div>
            </div>
          </div>

          {/* LEFT CALLOUT STACK */}
          {d.signals && (
            <div className="absolute top-32 left-8 z-30 space-y-1.5 max-w-[280px]">
              {d.signals?.slice(0, 3).map((signal, i) => (
                <div key={i} className="bg-black/85 backdrop-blur-sm px-3 py-1.5 rounded-md border-l-2 border-[#F97316]">
                  <div className="text-white text-xs font-bold">{typeof signal === "string" ? signal : signal.label || signal.text}</div>
                </div>
              ))}
            </div>
          )}

          {/* RUNNER TAGS */}
          {d.runners && (
            <div className="absolute inset-0 z-20 pointer-events-none">
              {d.runners.map((runner, i) => {
                const x = 15 + (runner.position - 1) * (70 / Math.max(d.runners.length - 1, 1));
                const y = 40 + (i % 3) * 8 + Math.sin(i * 1.2) * 5;
                return (
                  <div key={runner.number} className="absolute transition-all duration-500" style={{ left: `${x}%`, top: `${y}%` }}>
                    <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-black/80">
                      <span className="text-white text-[10px] font-black">{runner.number}</span>
                      <span className="text-white/80 text-[10px]">{(runner.name || runner.horse_name || "").split(" ").pop()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* FOOTER TRACK */}
          <div className="absolute bottom-0 left-0 right-0 z-30 h-16">
            <div className="mx-8 h-full bg-black/90 rounded-t-md overflow-hidden border-t border-white/10">
              <svg viewBox="0 0 1200 52" className="w-full h-full" preserveAspectRatio="none">
                <rect x="60" y="8" width="1080" height="36" rx="3" fill="rgba(255,255,255,0.03)" />
                <line x1="60" y1="26" x2="1140" y2="26" stroke="rgba(255,255,255,0.06)" strokeDasharray="12 6" />
                <line x1="1130" y1="8" x2="1130" y2="44" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeDasharray="3 2" />
                {d.runners?.map((runner, i) => {
                  const px = 70 + (d.runners.length - runner.position) / Math.max(d.runners.length - 1, 1) * 1050;
                  const py = 14 + (i % 3) * 12;
                  const effColour = runner.efficiency === "Strong" ? "#10B981" : runner.efficiency === "Weak" ? "#EF4444" : "#F59E0B";
                  return (
                    <g key={runner.number}>
                      <circle cx={px} cy={py} r={3.5} fill={effColour} opacity={0.9} />
                      <text x={px + 7} y={py + 3} fill="rgba(255,255,255,0.6)" fontSize="7" fontWeight="bold" fontFamily="monospace">{runner.number}</text>
                    </g>
                  );
                })}
                <text x="20" y="30" fill="rgba(255,255,255,0.15)" fontSize="8" fontFamily="monospace">START</text>
                <text x="1140" y="30" fill="rgba(255,255,255,0.15)" fontSize="8" fontFamily="monospace">FIN</text>
              </svg>
            </div>
          </div>
        </>
      )}

      {!d && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center text-white/30 bg-black/60 px-6 py-4 rounded-xl">
            <div className="w-8 h-8 border-2 border-[#EF4444] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <span className="text-xs font-mono">Loading broadcast feed...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// STEP 4: CYCLING VISION
// =============================================================================
const CyclingStep = ({ data }) => {
  const d = data;
  const banner = d?.banner;

  return (
    <div className="min-h-screen bg-[#090a0d] flex flex-col" data-testid="tour-cycling">
      {/* Step header */}
      <div className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-6 h-12 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bike className="w-4 h-4 text-[#A855F7]" />
          <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Cycling Vision</span>
          <span className="text-[#A855F7] text-xs font-bold">— Cross-Sport Proof</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#A855F7] animate-pulse" />
          <span className="text-[#A855F7] text-[10px] font-bold uppercase tracking-wider">Same Engine</span>
        </div>
      </div>

      {d ? (
        <div className="flex-1 p-6 max-w-5xl mx-auto w-full space-y-5">
          {/* Narrator */}
          <NarratorCallout
            visible
            text="Same analytical engine, different sport. Here it's tracking breakaway strength, peloton pressure, and chase momentum in a cycling stage."
            colour="#A855F7"
          />

          {/* Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <CyclingMetric label="Peloton Pressure" value={banner?.peloton_pressure} suffix="%" colour={banner?.peloton_pressure > 60 ? "#EF4444" : "#F59E0B"} icon={Target} />
            <CyclingMetric label="Breakaway Strength" value={banner?.breakaway_strength} suffix="%" colour={banner?.breakaway_strength > 70 ? "#10B981" : "#F59E0B"} icon={Zap} />
            <CyclingMetric label="Chase Momentum" value={banner?.chase_momentum} colour={banner?.chase_momentum === "Building" ? "#10B981" : "#F59E0B"} icon={TrendingUp} isText />
          </div>

          {/* Race Visual */}
          <div className="rounded-2xl border border-white/10 bg-[#0d0f12]/80 overflow-hidden h-64 relative">
            <svg className="w-full h-full" viewBox="0 0 800 300" preserveAspectRatio="none">
              <rect x="0" y="110" width="800" height="80" fill="rgba(255,255,255,0.03)" rx="4" />
              <line x1="0" y1="150" x2="800" y2="150" stroke="rgba(255,255,255,0.06)" strokeDasharray="20 10" />
              {d.riders?.map((rider, i) => {
                const isBreakaway = rider.type === "breakaway";
                const baseX = isBreakaway ? 520 + i * 35 : 180 + (i - 3) * 25;
                const baseY = 130 + (i % 3) * 25;
                const colour = isBreakaway ? "#A855F7" : "#3B82F6";
                return (
                  <g key={rider.id}>
                    <circle cx={baseX} cy={baseY} r={6} fill={colour} opacity={0.9} />
                    <rect x={baseX + 10} y={baseY - 8} width={50} height={16} rx={3} fill="rgba(0,0,0,0.8)" />
                    <text x={baseX + 14} y={baseY + 3} fill="white" fontSize="8" fontWeight="bold" fontFamily="monospace">R{rider.id}</text>
                    <text x={baseX + 36} y={baseY + 3} fill={rider.state === "Strong" ? "#10B981" : "#EF4444"} fontSize="7" fontFamily="monospace">
                      {rider.state === "Strong" ? "STR" : "FAD"}
                    </text>
                  </g>
                );
              })}
              <line x1="380" y1="125" x2="380" y2="175" stroke="#F97316" strokeWidth="1" strokeDasharray="4 3" />
              <rect x="355" y="140" width="50" height="18" rx="3" fill="rgba(249,115,22,0.2)" />
              <text x="366" y="153" fill="#F97316" fontSize="9" fontWeight="bold" fontFamily="monospace">{d.gap_seconds}s</text>
              <text x="160" y="210" fill="rgba(255,255,255,0.15)" fontSize="10" fontFamily="monospace">PELOTON</text>
              <text x="520" y="210" fill="rgba(255,255,255,0.15)" fontSize="10" fontFamily="monospace">BREAKAWAY</text>
              <text x="700" y="250" fill="rgba(255,255,255,0.2)" fontSize="10" fontFamily="monospace">{d.km_to_go} km to go</text>
            </svg>
          </div>

          {/* Key Insight */}
          <div className="p-4 rounded-xl border border-[#A855F7]/30 bg-[#A855F7]/5">
            <div className="text-[#A855F7] text-[10px] uppercase tracking-widest font-bold mb-1">Key Insight</div>
            <p className="text-white text-sm">{d.key_insight}</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <span className="text-white/40 text-xs font-mono">Initialising cycling data...</span>
          </div>
        </div>
      )}
    </div>
  );
};


// =============================================================================
// STEP 5: RACE LIBRARY
// =============================================================================
const LibraryStep = ({ data }) => {
  const detail = data?.detail;
  const raceCase = detail?.case;
  const events = detail?.events || [];
  const stories = detail?.stories || [];
  const totalRaces = data?.races?.length || 0;

  return (
    <div className="min-h-screen bg-[#090a0d] flex flex-col" data-testid="tour-library">
      {/* Step header */}
      <div className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-6 h-12 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#F59E0B]" />
          <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Race Library</span>
          <span className="text-[#F59E0B] text-xs font-bold">— The Intelligence Vault</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-white/30 text-xs font-mono">{totalRaces} races stored</span>
        </div>
      </div>

      {raceCase ? (
        <div className="flex-1 p-6 max-w-4xl mx-auto w-full space-y-5 overflow-y-auto">
          {/* Narrator */}
          <NarratorCallout
            visible
            text="Every race becomes a searchable intelligence case. The data doesn't vanish at the finish — it compounds into a knowledge library that gets smarter over time."
            colour="#F59E0B"
          />

          {/* Why This Race Was Won */}
          <div className="rounded-2xl border-2 border-[#F59E0B]/30 bg-[#F59E0B]/5 p-5" data-testid="tour-why-won">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-[#F59E0B] text-xs font-bold uppercase tracking-widest">Why This Race Was Won</span>
              <span className="text-white/20 text-[10px] ml-auto">{raceCase.track_name} · {raceCase.distance_m}m</span>
            </div>
            <p className="text-white text-sm leading-relaxed">{raceCase.why_won}</p>
          </div>

          {/* Replay Timeline — condensed for tour */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-3.5 h-3.5 text-[#F97316]" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">Replay Timeline</span>
            </div>
            <div className="space-y-1.5">
              {events.slice(0, 4).map((event, i) => (
                <div key={i} className="flex items-start gap-2.5 p-2 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="w-1 min-h-[24px] rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: event.colour }} />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold text-[11px]">{event.label}</span>
                      <span className="text-white/20 text-[10px] font-mono">{event.distance_marker_m}m</span>
                    </div>
                    <p className="text-white/40 text-[11px]">{event.explanation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Runner Stories — top 3 for tour */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-3.5 h-3.5 text-[#3B82F6]" />
              <span className="text-white text-xs font-bold uppercase tracking-wider">Runner Stories</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {stories.slice(0, 3).map((story) => (
                <div key={story.no} className="rounded-xl border border-white/10 bg-[#0d0f12]/80 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white font-bold text-xs">#{story.no} {story.horse}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{
                      backgroundColor: story.next === "Upgrade" ? "#10B98115" : story.next === "Downgrade" ? "#EF444415" : "#F59E0B15",
                      color: story.next === "Upgrade" ? "#10B981" : story.next === "Downgrade" ? "#EF4444" : "#F59E0B",
                    }}>{story.next}</span>
                  </div>
                  <div className="space-y-0.5 text-[10px]">
                    <div className="text-white/30"><span className="text-white/15 uppercase">Early:</span> {story.early}</div>
                    <div className="text-white/30"><span className="text-white/15 uppercase">Mid:</span> {story.mid}</div>
                    <div className="text-white/30"><span className="text-white/15 uppercase">Late:</span> {story.late}</div>
                  </div>
                  <div className="mt-2 pt-1.5 border-t border-white/5 text-white/50 text-[10px]">{story.summary}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <span className="text-white/40 text-xs font-mono">Loading race library...</span>
          </div>
        </div>
      )}
    </div>
  );
};



// =============================================================================
// STEP 5: INTELLIGENCE ASSIST LAYER
// =============================================================================
const AssistStep = ({ data }) => {
  const runner = data?.runner;
  const cards = data?.cards;

  return (
    <div className="min-h-screen bg-[#090a0d] flex flex-col" data-testid="tour-assist">
      {/* Step header */}
      <div className="bg-[#0d0f12]/95 backdrop-blur-sm border-b border-white/10 px-6 h-12 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-[#F97316]" />
          <span className="font-heading text-sm font-bold tracking-wider text-white uppercase">Intelligence Assist Layer</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#F97316] animate-pulse" />
          <span className="text-[#F97316] text-[10px] font-bold uppercase tracking-wider">Next Generation</span>
        </div>
      </div>

      <div className="flex-1 p-6 max-w-4xl mx-auto w-full space-y-6">
        {/* Narrator */}
        <NarratorCallout
          visible
          text="Most systems show you data. Better systems show you insights. We go further — we explain why each signal matters, what caused it, and what it means going forward."
          colour="#F97316"
        />

        {/* The hook */}
        <div className="text-center py-4">
          <h2 className="font-heading text-2xl font-bold text-white mb-2">Data &rarr; Insight &rarr; Explanation</h2>
          <p className="text-white/40 text-sm">Every signal gets a structured explanation. Cause. Effect. Implication.</p>
        </div>

        {runner ? (
          <div className="grid grid-cols-2 gap-6">
            {/* Left: The runner's signals */}
            <div className="space-y-4">
              <div className="rounded-xl border border-white/10 bg-[#0d0f12]/80 p-4">
                <div className="text-white/60 text-[10px] uppercase tracking-wider font-bold mb-3">Example Runner</div>
                <div className="text-white font-bold text-lg mb-1">#{runner.no} {runner.horse}</div>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {runner.eff && <SignalPill label="Efficiency" value={runner.eff} colour={runner.eff === "Strong" ? "#10B981" : runner.eff === "Weak" ? "#EF4444" : "#F59E0B"} />}
                  {runner.mom && <SignalPill label="Momentum" value={runner.mom} colour={runner.mom === "Building" ? "#10B981" : runner.mom === "Fading" || runner.mom === "Peaked Early" ? "#EF4444" : "#F59E0B"} />}
                  {runner.next && <SignalPill label="Next Start" value={runner.next} colour={runner.next === "Upgrade" ? "#10B981" : runner.next === "Downgrade" ? "#EF4444" : "#666"} />}
                </div>
                <div className="mt-3 text-white/40 text-xs">{runner.verdict}</div>
              </div>

              {/* What the intelligence engine sees */}
              {cards?.best_run && (
                <div className="rounded-xl border border-[#10B981]/20 bg-[#10B981]/5 p-4">
                  <div className="text-[#10B981] text-[10px] uppercase tracking-widest font-bold mb-1">Best Run Identified</div>
                  <div className="text-white font-bold text-sm">#{cards.best_run.saddle_number} {cards.best_run.horse_name}</div>
                  <div className="text-white/40 text-xs mt-1">{cards.best_run.verdict}</div>
                </div>
              )}
            </div>

            {/* Right: The IAL explanations */}
            <div className="space-y-3">
              <div className="text-[#F97316] text-[10px] uppercase tracking-widest font-bold mb-1">Intelligence Assist explains WHY</div>
              <InsightCardExpanded assist={runner.assist || []} />
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <span className="text-white/40 text-xs font-mono">Loading assist data...</span>
          </div>
        )}

        {/* Second narrator */}
        <NarratorCallout
          visible
          text="This builds trust, retention, and authority. Users don't just see what happened — they understand it."
          colour="#F97316"
        />
      </div>
    </div>
  );
};

const SignalPill = ({ label, value, colour }) => (
  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/5 border border-white/10">
    <span className="text-white/30 text-[9px] uppercase">{label}</span>
    <span className="text-xs font-bold" style={{ color: colour }}>{value}</span>
  </div>
);


// =============================================================================
// STEP 6: CLOSING
// =============================================================================
const ClosingStep = () => (
  <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="tour-closing">
    <div className="text-center max-w-xl animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex items-center justify-center gap-4 mb-10">
        <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center"><Activity className="w-5 h-5 text-[#F97316]" /></div>
        <div className="w-10 h-10 rounded-xl bg-[#EF4444]/10 flex items-center justify-center"><Tv className="w-5 h-5 text-[#EF4444]" /></div>
        <div className="w-10 h-10 rounded-xl bg-[#A855F7]/10 flex items-center justify-center"><Bike className="w-5 h-5 text-[#A855F7]" /></div>
        <div className="w-10 h-10 rounded-xl bg-[#F59E0B]/10 flex items-center justify-center"><BookOpen className="w-5 h-5 text-[#F59E0B]" /></div>
        <div className="w-10 h-10 rounded-xl bg-[#F97316]/10 flex items-center justify-center"><Lightbulb className="w-5 h-5 text-[#F97316]" /></div>
      </div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
        One engine, any sport.
      </h1>
      <p className="text-white/40 text-base leading-relaxed mb-3">
        Five views into one intelligence spine. Racing, broadcast, cross-sport, library, and explanation.
      </p>
      <p className="text-white/30 text-sm mb-10">
        Contact the person who shared this demo to discuss next steps.
      </p>
      <div className="inline-block px-4 py-2 rounded-lg border border-white/10 bg-white/5">
        <span className="text-[#F97316] text-xs font-bold uppercase tracking-widest">Race Intelligence Live</span>
      </div>
    </div>
  </div>
);

// =============================================================================
// SHARED SUB-COMPONENTS
// =============================================================================

const NarratorCallout = ({ visible, text, colour = "#F97316", inline = false }) => {
  if (!visible) return null;
  return (
    <div className={`animate-in fade-in slide-in-from-bottom-2 duration-500 ${inline ? "" : "mb-2"}`} data-testid="narrator-callout">
      <div className="flex items-start gap-3 p-3 rounded-lg border bg-black/60 backdrop-blur-sm" style={{ borderColor: `${colour}30` }}>
        <div className="w-1 h-full min-h-[20px] rounded-full flex-shrink-0" style={{ backgroundColor: colour }} />
        <p className="text-white/70 text-sm leading-relaxed">{text}</p>
      </div>
    </div>
  );
};

const TourIntelCard = ({ title, data, colour, icon: Icon }) => (
  <div className="p-3 rounded-xl border border-white/10 bg-[#0d0f12]/80">
    <div className="flex items-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5" style={{ color: colour }} />
      <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: colour }}>{title}</span>
    </div>
    {data ? (
      <>
        <div className="text-white font-bold text-sm mb-0.5">#{data.saddle_number} {data.horse_name}</div>
        <div className="text-white/40 text-[10px]">True {data.true_rank} · {data.efficiency_band} · {data.momentum_band}</div>
      </>
    ) : (
      <div className="text-white/20 text-xs">Not identified</div>
    )}
  </div>
);

const BroadcastChip = ({ label, value }) => (
  <div className="flex items-center gap-1.5">
    <span className="text-white/40 text-[10px] uppercase">{label}</span>
    <span className="text-white text-xs font-black uppercase">{value}</span>
  </div>
);

const CyclingMetric = ({ label, value, suffix = "", colour, icon: Icon, isText }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-[#0d0f12]/80 text-center">
    <div className="flex items-center justify-center gap-1.5 mb-2">
      <Icon className="w-3.5 h-3.5" style={{ color: colour }} />
      <span className="text-white/40 text-[10px] uppercase tracking-wider">{label}</span>
    </div>
    {isText ? (
      <div className="text-2xl font-bold font-heading" style={{ color: colour }}>{value}</div>
    ) : (
      <div className="text-3xl font-bold font-heading" style={{ color: colour }}>{Math.round(value || 0)}<span className="text-lg">{suffix}</span></div>
    )}
  </div>
);

export default GuidedTourPage;
